import { gql } from "graphql-request";

export const GET_TARIFF_ENGINEERING_CANDIDATES_QUERY = gql`
  query TariffEngineeringCandidates(
    $tenantId: ID
    $clientId: ID
    $filter: TariffEngineeringCandidateFilter
    $pageNo: Int
    $pageSize: Int
  ) {
    tariffEngineeringCandidates(
      tenantId: $tenantId
      clientId: $clientId
      filter: $filter
      pageNo: $pageNo
      pageSize: $pageSize
    ) {
      data {
        _id
        htsCode
        countryOfOrigin
        entryCount
        totalEnteredValue
        totalPaidDuty
        distinctManufacturerCount
        lastEntryDate
        currentDutyRate
        analysisStatus
        latestAnalysis {
          _id
          status
          finishedAt
          opportunities {
            strategy
            savingsPct
            confidence
          }
        }
      }
      meta {
        countDocs
        pageNo
        pageSize
      }
    }
  }
`;

export const GET_TARIFF_ENGINEERING_CANDIDATE_QUERY = gql`
  query TariffEngineeringCandidate(
    $htsCode: String!
    $countryOfOrigin: String!
    $tenantId: ID
    $clientId: ID
  ) {
    tariffEngineeringCandidate(
      htsCode: $htsCode
      countryOfOrigin: $countryOfOrigin
      tenantId: $tenantId
      clientId: $clientId
    ) {
      _id
      tenantId
      clientId
      htsCode
      countryOfOrigin
      entryCount
      totalEnteredValue
      totalPaidDuty
      distinctManufacturerCount
      lastEntryDate
      currentDutyRate
      currentDutyBreakdown {
        columnName
        authority
        rate
      }
      sampleSkus {
        sku
        name
        description
        declaredValue
      }
      applicableCh99Notes {
        ch99Code
        rateText
        intrinsicCondition
        oneLineSummary
        conditionHints {
          kind
          text
        }
      }
      analysisStatus
      latestAnalysis {
        _id
        status
        modelVersion
        startedAt
        finishedAt
        durationMs
        tokenUsage {
          prompt
          completion
          thinking
        }
        opportunities {
          strategy
          altHts
          altOrigin
          altCh99Code
          currentRate
          projectedRate
          savingsPct
          confidence
          rationale
          conditionQuote
          supportingRulings {
            id
            url
            summary
          }
          tradeDataEvidence {
            country
            percentShare
            totalValue
          }
          instructions
        }
        analystNotes
        toolCallLog {
          toolName
          summary
          durationMs
          ok
          error
        }
        error {
          message
          rawOutput
        }
      }
    }
  }
`;

export const GET_TARIFF_ENGINEERING_ANALYSIS_HISTORY_QUERY = gql`
  query TariffEngineeringAnalysisHistory(
    $htsCode: String!
    $countryOfOrigin: String!
    $productId: ID
    $tenantId: ID
    $clientId: ID
  ) {
    tariffEngineeringAnalysisHistory(
      htsCode: $htsCode
      countryOfOrigin: $countryOfOrigin
      productId: $productId
      tenantId: $tenantId
      clientId: $clientId
    ) {
      _id
      status
      modelVersion
      finishedAt
      durationMs
      opportunities {
        strategy
        savingsPct
        confidence
      }
    }
  }
`;

export const GET_TARIFF_ENGINEERING_PRODUCTS_QUERY = gql`
  query TariffEngineeringProducts(
    $tenantId: ID
    $clientId: ID
    $pageNo: Int
    $pageSize: Int
  ) {
    tariffEngineeringProducts(
      tenantId: $tenantId
      clientId: $clientId
      pageNo: $pageNo
      pageSize: $pageSize
    ) {
      data {
        _id
        sku
        name
        description
        htsCode
        countryOfOrigin
        metalBreakdown {
          steel
          aluminum
          copper
        }
        metalPercentage
        totalDutyRate
        additionalTariffs {
          columnId
          columnName
          rate
        }
        breakdownLines {
          lineType
          portion
          totalRate
          applicableRules {
            columnId
            columnName
            rate
          }
          calculationBreakdown {
            columnId
            columnName
            isStackable
            mutuallyExclusiveGroup
            applied
          }
          exclusionsApplied {
            listId
            listName
            columnId
            columnName
            authority
            matchedCode
          }
        }
        currentDutyRate
        analysisStatus
        latestAnalysis {
          _id
          status
          finishedAt
          opportunities {
            strategy
            savingsPct
            confidence
          }
        }
        createdAt
        updatedAt
      }
      meta {
        countDocs
        pageNo
        pageSize
      }
    }
  }
`;

export const GET_TARIFF_ENGINEERING_PRODUCT_QUERY = gql`
  query TariffEngineeringProduct(
    $productId: ID!
    $tenantId: ID
    $clientId: ID
  ) {
    tariffEngineeringProduct(
      productId: $productId
      tenantId: $tenantId
      clientId: $clientId
    ) {
      _id
      sku
      name
      description
      htsCode
      countryOfOrigin
      metalBreakdown {
        steel
        aluminum
        copper
      }
      metalPercentage
      totalDutyRate
      additionalTariffs {
        columnId
        columnName
        rate
      }
      breakdownLines {
        lineType
        portion
        totalRate
        applicableRules {
          columnId
          columnName
          rate
        }
        calculationBreakdown {
          columnId
          columnName
          isStackable
          mutuallyExclusiveGroup
          applied
        }
        exclusionsApplied {
          listId
          listName
          columnId
          columnName
          authority
          matchedCode
        }
      }
      currentDutyRate
      currentDutyBreakdown {
        columnName
        authority
        rate
      }
      attachedChatIds
      linkedChats {
        _id
        title
        category
        totalMessages
        lastActivityAt
        lastAiExcerpt
      }
      analysisStatus
      latestAnalysis {
        _id
        status
        modelVersion
        startedAt
        finishedAt
        durationMs
        tokenUsage {
          prompt
          completion
          thinking
        }
        opportunities {
          strategy
          altHts
          altOrigin
          altCh99Code
          currentRate
          projectedRate
          savingsPct
          confidence
          rationale
          conditionQuote
          supportingRulings {
            id
            url
            summary
          }
          tradeDataEvidence {
            country
            percentShare
            totalValue
          }
          instructions
        }
        analystNotes
        toolCallLog {
          toolName
          summary
          durationMs
          ok
          error
        }
        error {
          message
          rawOutput
        }
      }
      createdAt
      updatedAt
    }
  }
`;

export const GET_PRODUCT_ATTACHMENTS_QUERY = gql`
  query ProductAttachments($productId: ID!, $tenantId: ID, $clientId: ID) {
    productAttachments(
      productId: $productId
      tenantId: $tenantId
      clientId: $clientId
    ) {
      _id
      filename
      mimeType
      sizeBytes
      status
      summary
      caption
      error {
        code
        message
      }
      ingestedAt
      createdAt
      accessUrl
    }
  }
`;

export const GENERATE_PRODUCT_ATTACHMENT_UPLOAD_URL_MUTATION = gql`
  mutation GenerateProductAttachmentUploadUrl(
    $productId: ID!
    $filename: String!
    $mimeType: String!
    $sizeBytes: Int!
    $tenantId: ID
    $clientId: ID
  ) {
    generateProductAttachmentUploadUrl(
      productId: $productId
      filename: $filename
      mimeType: $mimeType
      sizeBytes: $sizeBytes
      tenantId: $tenantId
      clientId: $clientId
    ) {
      signedUrl
      s3Key
    }
  }
`;

export const CREATE_PRODUCT_ATTACHMENT_MUTATION = gql`
  mutation CreateProductAttachment(
    $productId: ID!
    $s3Key: String!
    $filename: String!
    $mimeType: String!
    $sizeBytes: Int!
    $tenantId: ID
    $clientId: ID
  ) {
    createProductAttachment(
      productId: $productId
      s3Key: $s3Key
      filename: $filename
      mimeType: $mimeType
      sizeBytes: $sizeBytes
      tenantId: $tenantId
      clientId: $clientId
    ) {
      _id
      filename
      mimeType
      sizeBytes
      status
      createdAt
    }
  }
`;

export const DELETE_PRODUCT_ATTACHMENT_MUTATION = gql`
  mutation DeleteProductAttachment(
    $attachmentId: ID!
    $tenantId: ID
    $clientId: ID
  ) {
    deleteProductAttachment(
      attachmentId: $attachmentId
      tenantId: $tenantId
      clientId: $clientId
    )
  }
`;

export const LINK_CHAT_TO_PRODUCT_MUTATION = gql`
  mutation LinkChatToProduct(
    $productId: ID!
    $chatId: ID!
    $tenantId: ID
    $clientId: ID
  ) {
    linkChatToProduct(
      productId: $productId
      chatId: $chatId
      tenantId: $tenantId
      clientId: $clientId
    ) {
      _id
      attachedChatIds
      linkedChats {
        _id
        title
        category
        totalMessages
        lastActivityAt
        lastAiExcerpt
      }
    }
  }
`;

export const UNLINK_CHAT_FROM_PRODUCT_MUTATION = gql`
  mutation UnlinkChatFromProduct(
    $productId: ID!
    $chatId: ID!
    $tenantId: ID
    $clientId: ID
  ) {
    unlinkChatFromProduct(
      productId: $productId
      chatId: $chatId
      tenantId: $tenantId
      clientId: $clientId
    ) {
      _id
      attachedChatIds
      linkedChats {
        _id
        title
        category
        totalMessages
        lastActivityAt
        lastAiExcerpt
      }
    }
  }
`;

export const GET_CHATS_FOR_PICKER_QUERY = gql`
  query ChatsForPicker($filter: ChatFilterInput) {
    chats(filter: $filter) {
      data {
        _id
        title
        category
        totalMessages
        lastActivityAt
      }
    }
  }
`;

export const RUN_TARIFF_ENGINEERING_RESEARCH_FOR_PRODUCT_MUTATION = gql`
  mutation RunTariffEngineeringResearchForProduct(
    $productId: ID!
    $tenantId: ID
    $clientId: ID
  ) {
    runTariffEngineeringResearchForProduct(
      productId: $productId
      tenantId: $tenantId
      clientId: $clientId
    ) {
      _id
      status
      finishedAt
      opportunities {
        strategy
        savingsPct
        confidence
      }
      error {
        message
      }
    }
  }
`;

export const RUN_TARIFF_ENGINEERING_RESEARCH_MUTATION = gql`
  mutation RunTariffEngineeringResearch(
    $htsCode: String!
    $countryOfOrigin: String!
    $tenantId: ID
    $clientId: ID
  ) {
    runTariffEngineeringResearch(
      htsCode: $htsCode
      countryOfOrigin: $countryOfOrigin
      tenantId: $tenantId
      clientId: $clientId
    ) {
      _id
      status
      htsCode
      countryOfOrigin
      finishedAt
      opportunities {
        strategy
        savingsPct
        confidence
      }
      error {
        message
      }
    }
  }
`;
